import React from 'react';
import I18n from 'i18n-js';

import { NudgeExploreIcon, PencilSimpleIcon } from '@reverbdotcom/cadence/icons/react';
import { RCButton, RCGuidePopover, RCTextWithIcon, RCToast } from '@reverbdotcom/cadence/components';
import { listingPriceRecommendation } from './quick_price_edit_modal';
import { useCreateUserAction, createUserActionMutation } from '@reverbdotcom/commons/src/user_actions/create_action_mutation';
import { core_apimessages_UserActionName, Listing, core_apimessages_Money, reverb_pricing_PriceRecommendation } from '@reverbdotcom/commons/src/gql/graphql';
import { PriceDisplay } from '@reverbdotcom/commons';
import SellerListingsCollectionContext from '../../listings_management/SellerListingsCollectionContext';
import { parseAmountCents } from '@reverbdotcom/commons/src/money';
import { useMutation } from '@reverbdotcom/commons/src/useMutation';
import QuickPriceEditModalV2 from './QuickPriceEditModalV2';
import ListingsManagementPageStore from '../../listings_management/listings_management_page_store';
import ListingsManagementPageActions from '../../listings_management/listings_management_page_actions';

const LIVE = 'live';
const DRAFT = 'draft';
const ENDED = 'ended';

export const VALID_LISTING_STATES_FOR_QUICK_PRICE_EDIT = [
  LIVE,
  DRAFT,
  ENDED,
];

export const UK_FEE_POPOVER_ID = 'uk_fee_change_data_informed_guidance';

interface IExternalProps {
  listing: Listing;
  showQuickPriceEditPopover?: boolean;
  isEditable?: boolean;
  hidePriceRecommendationCallout?: boolean;
}

export const COMPONENT_NAME = 'QuickPriceEditButton';

function VatIncludedHint({ vatIncludedHint }) {
  return (
    <div className="fg-color-text-secondary size-50 weight-normal align-right mb-2">
      {vatIncludedHint}
    </div>
  );
}

interface IPricedAboveAverageArgs {
  sellerPrice: core_apimessages_Money;
  conditionSlug?: string;
  priceRecommendation?: reverb_pricing_PriceRecommendation;
}

export function isPricedAboveAverage({
  sellerPrice,
  priceRecommendation,
}: IPricedAboveAverageArgs) {
  const { priceMiddle } = priceRecommendation ?? {};
  if (!priceMiddle || !sellerPrice) return false;
  return priceMiddle.amountCents < sellerPrice.amountCents;
}

export function listingSellerPrice(listing: Listing, defaultCurrency?: string) {
  const {
    shop = {},
    sellerPrice,
  } = listing ?? {};

  const shopCurrency = shop.currency ?? defaultCurrency;
  return sellerPrice ?? parseAmountCents(0, shopCurrency, { precision: 0 });
}

export default function QuickPriceEditButton({
  listing,
  showQuickPriceEditPopover,
  isEditable = true,
  hidePriceRecommendationCallout = false,
}: IExternalProps) {
  const sellerPrice = listingSellerPrice(listing);

  const {
    useLazySellerListingsQuery,
    listingsCollectionState,
  } = React.useContext(SellerListingsCollectionContext);
  const { listings } = listingsCollectionState || { listings: [] };

  const { refetchSellerListings } = useLazySellerListingsQuery();

  const [showToast, setShowToast] = React.useState(false);
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [showPopover, setShowPopover] = React.useState(showQuickPriceEditPopover);

  const [listingsManagementPageState, setListingsManagementPageState] = React.useState(ListingsManagementPageStore.getState());
  const showUKFeeChangePopover = listingsManagementPageState?.currentPopupId === UK_FEE_POPOVER_ID;

  React.useEffect(() => {
    const onChange = () => {
      setListingsManagementPageState(ListingsManagementPageStore.getState());
    };
    ListingsManagementPageStore.listen(onChange);
    return () => {
      ListingsManagementPageStore.unlisten(onChange);
    };
  }, []);

  const priceRecommendation = listingPriceRecommendation(listing);
  const { priceMiddle } = priceRecommendation;

  const showPricedAboveAverageCallout = isEditable && isPricedAboveAverage({
    sellerPrice,
    priceRecommendation,
  });

  const [createMyAction] = useMutation(createUserActionMutation);
  const { createUserAction } = useCreateUserAction(
    core_apimessages_UserActionName.QUICK_PRICE_EDIT_CALLOUT,
    {
      uuid: listing.sellerUuid,
    },
    {
      listingId: listing.id,
    },
    createMyAction,
  );
  const { createUserAction: createUKFeeChangeUserAction } = useCreateUserAction(
    core_apimessages_UserActionName.UK_FEE_CHANGE_CALLOUT,
    {
      uuid: listing.sellerUuid,
    },
    {
      listingId: null,
    },
    createMyAction,
  );

  const searchParams = new URLSearchParams(window.location.search);
  const page = searchParams.get('page') ?? 1;
  const isTheFirstListing = page == 1 && listings?.[0]?.id === listing.id;

  React.useEffect(() => {
    return () => {
      setShowPopover(false);
    };
  }, [searchParams.toString()]);

  React.useEffect(() => {
    if (isTheFirstListing && !showPricedAboveAverageCallout) {
      ListingsManagementPageActions.removePopup(UK_FEE_POPOVER_ID);
    }
  }, [isTheFirstListing, showPricedAboveAverageCallout]);

  return (
    <div className="quick-price-edit-button">
      <RCGuidePopover
        title={I18n.t('discovery.listingsManagement.quickPriceEdit.popover.title')}
        content={I18n.t('discovery.listingsManagement.quickPriceEdit.popover.content')}
        isOpen={isEditable && showPopover}
        onDismiss={() => {
          setShowPopover(false);
          createUserAction();
        }}
        preventAutoFocus
        anchor={<div className="size-100 weight-bold">
          {isEditable ? (
            <RCButton
              variant="muted"
              size="mini"
              onClick={() => setIsModalOpen(true)}
              leadingIcon={<PencilSimpleIcon />}
            >
              {sellerPrice.amountCents === 0
                ? I18n.t(
                  'discovery.listingsManagement.quickPriceEdit.button.addPrice',
                )
                : sellerPrice.display}
            </RCButton>
          ) : (
            <div className="mt-1">
              <PriceDisplay
                amountCents={sellerPrice.amountCents}
                display={sellerPrice.display}
              />
            </div>
          )}
        </div>}
      />

      {showPricedAboveAverageCallout && !hidePriceRecommendationCallout && (
        <RCGuidePopover
          isOpen={showUKFeeChangePopover && isTheFirstListing}
          onDismiss={() => {
            ListingsManagementPageActions.closePopup(UK_FEE_POPOVER_ID);
            createUKFeeChangeUserAction();
          }}
          title={I18n.t('discovery.listingsManagement.quickPriceEdit.ukFeeChangePopover.title')}
          content={I18n.t('discovery.listingsManagement.quickPriceEdit.ukFeeChangePopover.content')}
          position="bottom-start"
          anchor={
            <div className="quick-price-edit-button__priced-above-average">
              <RCTextWithIcon
                size="large"
                icon={<NudgeExploreIcon />}
                placement="left"
              >
                {I18n.t('discovery.listingsManagement.quickPriceEdit.button.mostLikelyToSellPrice', {
                  displayPrice: parseAmountCents(
                    priceMiddle?.amountCents,
                    priceMiddle?.currency,
                    { precision: 0 },
                  ).display,
                })}
              </RCTextWithIcon>
            </div>
          }
        />
      )}

      <QuickPriceEditModalV2
        isOpen={isModalOpen}
        listingId={listing.id}
        requestClose={() => setIsModalOpen(false)}
        onSuccessCallback={() => {
          setIsModalOpen(false);
          setShowToast(true);
          refetchSellerListings([listing.id]);
        }}
      />

      <RCToast
        isOpen={showToast}
        onClose={() => setShowToast(false)}
        text={I18n.t('discovery.listingsManagement.toast.message.success')}
        theme="success"
        delayInterval="short"
      />

      {listing.vatIncluded &&
        <VatIncludedHint vatIncludedHint={listing.vatIncludedHint} />
      }
    </div>
  );
}
